import { FaTrash } from 'react-icons/fa6';
import MiniLoading from '../../../ions/MiniLoading';
import type { FinancialRepresentative } from '../../../../services/personApi';
import { maskPhone } from '../../../../utils/phoneUtils';
import { formatCpf } from '../../../../utils/taxIdUtils';

const EMPTY_VALUE = '—';
const EMPTY_LIST_MESSAGE = 'Nenhum representante financeiro vinculado.';
const HEADER_CELL = 'whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400';
const BODY_CELL = 'whitespace-nowrap px-3 py-2 text-sm text-gray-700 dark:text-gray-200';
const LOADING_MESSAGE = 'Carregando representantes financeiros...';
const LOAD_ERROR_MESSAGE = 'Não foi possível carregar os representantes financeiros.';

export const FinancialRepresentativesLoadState = {
    LOADING: 'loading',
    FAILED: 'failed',
    LOADED: 'loaded',
} as const satisfies Record<string, string>;

export type FinancialRepresentativesLoadState = typeof FinancialRepresentativesLoadState[keyof typeof FinancialRepresentativesLoadState];

interface Props {
    items: FinancialRepresentative[];
    loadState?: FinancialRepresentativesLoadState;
    onRemove?: (individualUuid: string) => void;
}

interface RowProps {
    item: FinancialRepresentative;
    onRemove?: (individualUuid: string) => void;
}

const displayPhone = (phone: string | null): string => (phone ? maskPhone(phone) : EMPTY_VALUE);

/**
 * Load state of the edit page: still loading until the GET answers, failed when it did not.
 */
export const toFinancialRepresentativesLoadState = (loaded: boolean, failed: boolean): FinancialRepresentativesLoadState => {
    if (loaded) return FinancialRepresentativesLoadState.LOADED;
    if (failed) return FinancialRepresentativesLoadState.FAILED;
    return FinancialRepresentativesLoadState.LOADING;
};

const LoadingMessage = () => (
    <p role="status" className="flex items-center text-sm text-gray-500 dark:text-gray-400">
        <MiniLoading />
        {LOADING_MESSAGE}
    </p>
);

const LoadErrorMessage = () => (
    <p role="alert" className="text-sm text-red-600 dark:text-red-400">{LOAD_ERROR_MESSAGE}</p>
);

const FinancialRepresentativeRow = ({ item, onRemove }: RowProps) => (
    <tr className="border-t border-gray-200 hover:bg-gray-50 dark:border-slate-700 dark:hover:bg-slate-700">
        <td className={`${BODY_CELL} font-mono`}>{formatCpf(item.socialId)}</td>
        <td className={BODY_CELL}>{item.name}</td>
        <td className={BODY_CELL}>{displayPhone(item.phone)}</td>
        <td className={BODY_CELL}>{item.email ?? EMPTY_VALUE}</td>
        {onRemove ? (
            <td className={`${BODY_CELL} w-10 text-right`}>
                <button
                    type="button"
                    onClick={() => onRemove(item.individualUuid)}
                    aria-label={`Remover ${item.name}`}
                    title="Remover"
                    className="rounded-lg p-1.5 text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900 transition-colors"
                >
                    <FaTrash size={11} />
                </button>
            </td>
        ) : null}
    </tr>
);

/**
 * Financial Representatives of a legal person: CPF, Nome, Telefone and E-mail (AF-4128). The trash
 * column shows only when `onRemove` is given; read-only screens leave it out.
 */
const FinancialRepresentativesTable = ({ items, loadState = FinancialRepresentativesLoadState.LOADED, onRemove }: Props) => {
    if (loadState === FinancialRepresentativesLoadState.LOADING) return <LoadingMessage />;
    if (loadState === FinancialRepresentativesLoadState.FAILED) return <LoadErrorMessage />;
    if (items.length === 0) {
        return <p className="text-sm text-gray-500 dark:text-gray-400">{EMPTY_LIST_MESSAGE}</p>;
    }
    return (
        <div className="overflow-x-auto rounded border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
            <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-slate-900">
                    <tr>
                        <th scope="col" className={HEADER_CELL}>CPF</th>
                        <th scope="col" className={HEADER_CELL}>Nome</th>
                        <th scope="col" className={HEADER_CELL}>Telefone</th>
                        <th scope="col" className={HEADER_CELL}>E-mail</th>
                        {onRemove ? <th scope="col" className={HEADER_CELL}><span className="sr-only">Ações</span></th> : null}
                    </tr>
                </thead>
                <tbody>
                    {items.map((item) => (
                        <FinancialRepresentativeRow key={item.individualUuid} item={item} onRemove={onRemove} />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default FinancialRepresentativesTable;
